import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  Dispatch,
  RefObject,
  SetStateAction,
  useMemo,
  useState,
} from "react";

import {
  AvailablePaymentCurrency,
  DonationFormCategory,
  FormStatus,
  getCurrencyFromCountry,
  hasMultipleLocales,
  Locales,
  TicketingFormCategory,
  ProductDonationRecurrenceInterval,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { useIsOnboarding } from "../../../../common/hooks/useIsOnboarding";
import {
  LiveEditorStep,
  LiveEditorStepKey,
  coerceLegacyTicketingStepKey,
  coerceRemovedStepKey,
  isLegacyTicketingStepKey,
  isRemovedStepKey,
  isValidStepKey,
} from "../Steps/LiveEditorStep.enum";

import { getShowRenewalEmails } from "./getShowRenewalEmails";
import { useInvalidateEditedFormQuery } from "./useInvalidateEditedFormQuery";
import { LiveFormRemindersState, useLiveFormRemindersData } from "./useLiveFormRemindersData";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { FormStepKey } from "@/canvas/features/FormV2/steps/FormStepKey";
import { DonationFormWithUserOrOrgAccessOutput, TicketingOutput } from "@/common/types/trpc";
import { useStepNavigation } from "@/components/LiveEditorElements/Navigation/useStepNavigation";
import { FurthestStepProgress, useStepProgress } from "@/features/LiveFormEditor/Navigation/useStepProgress";
import {
  LiveDonationData,
  LiveFormEditorMode,
  LiveTicketingData,
} from "@/features/LiveFormEditor/types/LiveFormEditor.types";

export type PreviewType = "form" | "email" | "e-ticket" | "tax-receipt" | "membership-card" | "campaign-life-cycle";

export type SaveFormModalState = {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  closeModalAndExecuteActions: () => void;
};

export type ShareFormModalState = {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
};

export type FormType = "ticketing" | "donation-form";

export type RemindersData = LiveFormRemindersState;

export interface LiveFormEditorFeatureFlags {
  isDelightfulPagesActive: boolean;
  isDelightfulPagesLoading: boolean;
  isAddOnsStepOn: boolean;
  isAddOnsStepLoading: boolean;
  isCampaignLifeCycleStepOn: boolean;
  isCampaignLifeCycleStepLoading: boolean;
  isCustomColorTileActive: boolean;
  isCustomColorTileLoading: boolean;
}

/** When set from the editor (e.g. when focusing an amount label field), the donation preview will select this tab and amount so the label is visible. Uses amountIndex because preview amounts get new ids on each update. */
export type PreviewAmountFocus = {
  recurrenceInterval: `${ProductDonationRecurrenceInterval}`;
  amountIndex: number;
} | null;

export interface LiveFormEditorContextType<FormTypeDiscriminator extends FormType = FormType> {
  currentStepIndex: LiveEditorStep;
  currentStepKey: LiveEditorStepKey;
  updateCurrentStep: (stepKey: LiveEditorStepKey) => void;
  totalNumberOfSteps: number;
  liveEditorStepsKeys: LiveEditorStepKey[];
  getIndexStepFromKey: (key: LiveEditorStepKey) => LiveEditorStep;
  getKeyStepFromIndex: (index: LiveEditorStep) => LiveEditorStepKey;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  stepProgress: FurthestStepProgress;
  markStepCompleted: (stepIndex: LiveEditorStep, stepKey: LiveEditorStepKey) => void;
  apiFormObject: FormTypeDiscriminator extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput;
  updateApiFormObject: (
    formObject: Partial<
      FormTypeDiscriminator extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput
    >
  ) => void;
  liveFormData?: FormTypeDiscriminator extends "ticketing" ? LiveTicketingData : LiveDonationData;
  setLiveFormData: (data: FormTypeDiscriminator extends "ticketing" ? LiveTicketingData : LiveDonationData) => void;
  previewDevice: "mobile" | "desktop";
  setPreviewDevice: (mode: "mobile" | "desktop") => void;
  previewScale: number;
  setPreviewScale: (scale: number) => void;
  setRefreshArrowPosition: (callback: () => void) => void;
  refreshArrowPosition: () => void;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  previewType: PreviewType;
  saveCurrentStep: RefObject<() => Promise<boolean>>;
  isStepChanging: boolean;
  setIsStepChanging: (isStepChanging: boolean) => void;
  editOrCreateMode: "edit" | "create";
  setEditOrCreateMode: (mode: "edit" | "create") => void;
  isFormDirty: boolean;
  setIsFormDirty: (isDirty: boolean) => void;
  resetForm: RefObject<() => void>;
  isNavigationOpen: boolean;
  setIsNavigationOpen: (isNavigationOpen: boolean) => void;
  isOnboarding: boolean;
  saveFormModalState: SaveFormModalState;
  shareFormModalState: ShareFormModalState;
  performActionWithSaveGuard: (params: { action: () => void | Promise<void> }) => void;
  formType: FormTypeDiscriminator extends "ticketing" ? "ticketing" : "donation-form";
  formCategory: FormTypeDiscriminator extends "ticketing" ? TicketingFormCategory : DonationFormCategory;
  formCurrency: AvailablePaymentCurrency;
  isTicketingClosed?: boolean;
  previewStepKey: FormStepKey;
  setPreviewStepKey: (stepKey: FormStepKey) => void;
  /** When set, the donation form preview will select this recurrence tab and amount (so the impact label is visible). Used when focusing an amount label field in the editor. */
  previewAmountFocus: PreviewAmountFocus;
  setPreviewAmountFocus: (focus: PreviewAmountFocus) => void;
  // Prize the admin last interacted with in the prizes step, shown selected in the ticketing preview.
  previewPrizeFocusId: string | null;
  setPreviewPrizeFocusId: (prizeId: string | null) => void;
  remindersData: RemindersData;
  showRenewalEmails: boolean;
  isMobilePreviewOpen: boolean;
  setIsMobilePreviewOpen: (isOpen: boolean) => void;
  footerPortalNode: HTMLDivElement | null;
  setFooterPortalNode: (node: HTMLDivElement | null) => void;
  isCreatedFromDuplicate: boolean;
  editorMode: LiveFormEditorMode;
  optionalFundraiserToken?: string; // only set when editorMode is "fundraiserToken"
  defaultExitUrl?: string;
  featureFlags: LiveFormEditorFeatureFlags;
  formHasTranslation: boolean;
  secondaryLocale: Locales | null;
}

const defaultValues: LiveFormEditorContextType<"ticketing"> = {
  currentStepIndex: 0,
  currentStepKey: "title",
  updateCurrentStep: () => {},
  totalNumberOfSteps: 0,
  liveEditorStepsKeys: [],
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  stepProgress: { reached: 1, saved: 1 },
  markStepCompleted: () => {},
  getIndexStepFromKey: () => 0,
  getKeyStepFromIndex: () => "title",
  apiFormObject: {} as TicketingOutput,
  updateApiFormObject: () => {},
  liveFormData: undefined,
  setLiveFormData: () => {},
  previewDevice: "mobile",
  setPreviewDevice: () => {},
  previewScale: 1,
  setPreviewScale: () => {},
  setRefreshArrowPosition: () => {},
  refreshArrowPosition: () => {},
  iframeRef: { current: null },
  previewType: "form",
  saveCurrentStep: { current: () => Promise.resolve(true) },
  isStepChanging: false,
  setIsStepChanging: () => {},
  editOrCreateMode: "edit",
  setEditOrCreateMode: () => {},
  saveFormModalState: {
    isOpen: false,
    openModal: () => {},
    closeModal: () => {},
    closeModalAndExecuteActions: () => {},
  },
  shareFormModalState: {
    isOpen: false,
    openModal: () => {},
    closeModal: () => {},
  },
  isFormDirty: false,
  setIsFormDirty: () => {},
  resetForm: { current: () => {} },
  isNavigationOpen: false,
  setIsNavigationOpen: () => {},
  isOnboarding: false,
  performActionWithSaveGuard: () => {},
  formType: "ticketing",
  formCategory: TicketingFormCategory.Event,
  formCurrency: AvailablePaymentCurrency.Usd,
  previewStepKey: "selection",
  setPreviewStepKey: () => {},
  previewAmountFocus: null,
  setPreviewAmountFocus: () => {},
  previewPrizeFocusId: null,
  setPreviewPrizeFocusId: () => {},
  remindersData: {
    showReminders: false,
  },
  showRenewalEmails: false,
  isMobilePreviewOpen: false,
  setIsMobilePreviewOpen: () => {},
  footerPortalNode: null,
  setFooterPortalNode: () => {},
  isCreatedFromDuplicate: false,
  editorMode: "org",
  optionalFundraiserToken: undefined,
  defaultExitUrl: undefined,
  featureFlags: {
    isDelightfulPagesActive: false,
    isDelightfulPagesLoading: false,
    isAddOnsStepOn: false,
    isAddOnsStepLoading: false,
    isCampaignLifeCycleStepOn: false,
    isCampaignLifeCycleStepLoading: false,
    isCustomColorTileActive: false,
    isCustomColorTileLoading: false,
  },
  formHasTranslation: false,
  secondaryLocale: null,
};

export const LiveFormEditorContext = createContext<LiveFormEditorContextType<FormType>>(
  defaultValues as LiveFormEditorContextType<FormType>
);

export type StableLiveFormEditorContextType<F extends FormType> = Pick<
  LiveFormEditorContextType<F>,
  | "apiFormObject"
  | "updateApiFormObject"
  | "formHasTranslation"
  | "secondaryLocale"
  | "editOrCreateMode"
  | "isOnboarding"
  | "currentStepKey"
  | "formCategory"
>;

export const StableLiveFormEditorContext = createContext<StableLiveFormEditorContextType<FormType>>(
  defaultValues as StableLiveFormEditorContextType<FormType>
);

interface LiveFormEditorProviderProps<F extends FormType> {
  formType: F;
  steps: { key: LiveEditorStepKey; shouldBeSkip?: boolean }[];
  liveFormData: F extends "ticketing" ? LiveTicketingData | null : LiveDonationData | null;
  apiFormObject: F extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput;
  editOrCreateMode: "edit" | "create";
  setLiveFormData: (data: F extends "ticketing" ? LiveTicketingData : LiveDonationData) => void;
  setApiFormObject: Dispatch<
    SetStateAction<F extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput>
  >;
  setEditOrCreateMode: (mode: "edit" | "create") => void;
  children: (params: LiveFormEditorContextType<F>) => React.ReactNode;
  formCategory: F extends "ticketing" ? TicketingFormCategory : DonationFormCategory;
  isTicketingClosed?: boolean;
  editorMode?: LiveFormEditorMode;
  optionalFundraiserToken?: string; // only set when editorMode is "fundraiserToken"
  defaultExitUrl?: string;
  featureFlags: LiveFormEditorFeatureFlags;
}

export const LiveFormEditorProvider = <F extends FormType>({
  children,
  steps,
  liveFormData,
  apiFormObject,
  editOrCreateMode,
  setLiveFormData,
  setApiFormObject,
  setEditOrCreateMode,
  formType,
  formCategory,
  isTicketingClosed,
  editorMode = "org",
  optionalFundraiserToken,
  defaultExitUrl,
  featureFlags,
}: LiveFormEditorProviderProps<F>) => {
  const searchParams = useSearchParams();

  const validateStepKey = useCallback(
    (key: unknown): key is LiveEditorStepKey => !isLegacyTicketingStepKey(key, formType) && isValidStepKey(key),
    [formType]
  );

  useEffect(() => {
    if (isRemovedStepKey(window.location.hash.slice(1))) {
      window.location.hash = coerceRemovedStepKey(window.location.hash.slice(1));
    }
  }, []);

  const rawLastSavedDraftStep = apiFormObject.status === "draft" ? apiFormObject.liveFormEditorStep : null;
  const lastSavedDraftStep =
    featureFlags.isCampaignLifeCycleStepOn && !apiFormObject.editorFurthestStepSaved
      ? "campaign-life-cycle"
      : coerceRemovedStepKey(coerceLegacyTicketingStepKey(rawLastSavedDraftStep, formType));

  const {
    currentStepIndex,
    currentStepKey,
    updateCurrentStep,
    goToNextStep,
    goToPreviousStep,
    getIndexStepFromKey,
    getKeyStepFromIndex,
    liveEditorStepsKeys,
  } = useStepNavigation<LiveEditorStepKey>({
    steps,
    lastSavedDraftStep,
    isValidStepKey: validateStepKey,
    fallbackStepKey: "title",
  });

  const { stepProgress, markStepAsSaved } = useStepProgress({
    currentStepIndex,
    apiFormObject,
    currentStepKey,
    getIndexStepFromKey,
    formType,
  });

  const formCurrency = useMemo(
    () => getCurrencyFromCountry(apiFormObject.organization?.country),
    [apiFormObject.organization?.country]
  );

  const formFields =
    formType === "ticketing"
      ? (apiFormObject as TicketingOutput).ticketingFields
      : (apiFormObject as DonationFormWithUserOrOrgAccessOutput).donationFormFields;
  const secondaryLocale = formFields?.find((field) => field.locale !== apiFormObject.locale)?.locale ?? null;
  const formHasTranslation = secondaryLocale !== null && hasMultipleLocales(apiFormObject.organization?.country);

  const [previewDevice, setPreviewDevice] = useState<"mobile" | "desktop">("mobile");
  const [previewScale, setPreviewScale] = useState<number>(1);
  const [refreshArrowPosition, setRefreshArrowPosition] = useState<() => void>(() => {});
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const saveCurrentStep = useRef<() => Promise<boolean>>(() => Promise.resolve(true));
  const [isFormDirty, setIsFormDirty] = useState(false);
  const resetForm = useRef<() => void>(() => {});

  const isDeepLinkedToCard = searchParams.get("openedCard") !== null;
  const [isNavigationOpen, setIsNavigationOpen] = useState(!isDeepLinkedToCard);

  const previewType = getPreviewType(currentStepKey, formCategory);

  const totalNumberOfSteps = liveEditorStepsKeys.length;

  const { isOnboarding } = useIsOnboarding();

  const [isSaveFormModalOpen, setIsSaveFormModalOpen] = useState(false);
  const pendingActionRef = useRef<() => void>(() => {});
  const saveFormModalState: SaveFormModalState = {
    isOpen: isSaveFormModalOpen,
    openModal: () => setIsSaveFormModalOpen(true),
    closeModal: () => setIsSaveFormModalOpen(false),
    closeModalAndExecuteActions: () => {
      setIsSaveFormModalOpen(false);
      pendingActionRef.current();
      pendingActionRef.current = () => {};
    },
  };
  const performActionWithSaveGuard = ({ action }: { action: () => void | Promise<void> }) => {
    // If the form is dirty, we open the save modal to ask confirmation to save the changes before executing the callback.
    if (isFormDirty || apiFormObject.status === FormStatus.Draft) {
      pendingActionRef.current = action;
      setIsSaveFormModalOpen(true);
    } else {
      // If the form is not dirty, we directly execute the callback without opening the save modal.
      action();
    }
  };

  const [isShareFormModalOpen, setIsShareFormModalOpen] = useState(false);
  const shareFormModalState: ShareFormModalState = {
    isOpen: isShareFormModalOpen,
    openModal: () => setIsShareFormModalOpen(true),
    closeModal: () => setIsShareFormModalOpen(false),
  };

  const [isStepChanging, setIsStepChanging] = useState(false);

  const [previewStepKey, setPreviewStepKey] = useState<FormStepKey>("selection");
  const [previewAmountFocus, setPreviewAmountFocus] = useState<PreviewAmountFocus>(null);
  const [previewPrizeFocusId, setPreviewPrizeFocusId] = useState<string | null>(null);
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  const [footerPortalNode, setFooterPortalNode] = useState<HTMLDivElement | null>(null);

  const remindersData = useLiveFormRemindersData({
    formCategory,
    recurrences: formType === "ticketing" ? (apiFormObject as TicketingOutput).recurrences : undefined,
    ticketingId: apiFormObject.id,
  });

  const showRenewalEmails = getShowRenewalEmails({
    formType,
    formCategory,
    apiFormObject,
  });

  const invalidateEditedFormQuery = useInvalidateEditedFormQuery({ formType, formId: apiFormObject.id });

  const updateApiFormObject = useCallback(
    (formObject: Partial<F extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput>) => {
      setApiFormObject((previous) => ({ ...previous, ...formObject }));
      void invalidateEditedFormQuery();
    },
    [setApiFormObject, invalidateEditedFormQuery]
  );

  const contextValue: LiveFormEditorContextType<F> = {
    currentStepIndex,
    currentStepKey,
    updateCurrentStep,
    goToNextStep,
    goToPreviousStep,
    stepProgress,
    markStepCompleted: markStepAsSaved,
    liveEditorStepsKeys,
    apiFormObject,
    updateApiFormObject,
    liveFormData: liveFormData as F extends "ticketing" ? LiveTicketingData : LiveDonationData,
    setLiveFormData,
    previewDevice,
    setPreviewDevice,
    previewScale,
    setPreviewScale,
    setRefreshArrowPosition,
    refreshArrowPosition,
    iframeRef,
    previewType,
    saveCurrentStep,
    isStepChanging,
    setIsStepChanging,
    editOrCreateMode,
    setEditOrCreateMode,
    saveFormModalState,
    shareFormModalState,
    isFormDirty,
    setIsFormDirty,
    resetForm,
    isNavigationOpen,
    setIsNavigationOpen,
    totalNumberOfSteps,
    isOnboarding,
    performActionWithSaveGuard,
    getIndexStepFromKey,
    getKeyStepFromIndex,
    formType: formType as never,
    formCategory,
    formCurrency,
    isTicketingClosed,
    previewStepKey,
    setPreviewStepKey,
    previewAmountFocus,
    setPreviewAmountFocus,
    previewPrizeFocusId,
    setPreviewPrizeFocusId,
    remindersData,
    showRenewalEmails,
    isMobilePreviewOpen,
    setIsMobilePreviewOpen,
    footerPortalNode,
    setFooterPortalNode,
    isCreatedFromDuplicate: Boolean(apiFormObject.duplicatedFromId),
    editorMode,
    optionalFundraiserToken,
    defaultExitUrl,
    featureFlags,
    formHasTranslation,
    secondaryLocale,
  };

  const stableContextValue = useMemo(
    () => ({
      apiFormObject,
      updateApiFormObject,
      formHasTranslation,
      secondaryLocale,
      editOrCreateMode,
      isOnboarding,
      currentStepKey,
      formCategory,
    }),
    [
      apiFormObject,
      updateApiFormObject,
      formHasTranslation,
      secondaryLocale,
      editOrCreateMode,
      isOnboarding,
      currentStepKey,
      formCategory,
    ]
  ) as unknown as StableLiveFormEditorContextType<FormType>;

  return (
    <LiveFormEditorContext.Provider value={contextValue}>
      <StableLiveFormEditorContext.Provider value={stableContextValue}>
        {children(contextValue)}
      </StableLiveFormEditorContext.Provider>
    </LiveFormEditorContext.Provider>
  );
};

export const useLiveFormEditorContext = <F extends FormType>() => {
  return useContext(LiveFormEditorContext) as LiveFormEditorContextType<F>;
};

export const useStableLiveFormEditorContext = <F extends FormType>() => {
  return useContext(StableLiveFormEditorContext) as StableLiveFormEditorContextType<F>;
};

const getPreviewType = (
  currentStepKey: LiveEditorStepKey,
  formCategory: TicketingFormCategory | DonationFormCategory
): PreviewType => {
  if (
    currentStepKey === "thank-you-email" ||
    currentStepKey === "thank-you-email-not-winner" ||
    currentStepKey === "automated-communications"
  ) {
    return "email";
  }
  if (currentStepKey === "e-ticket") {
    if (formCategory === TicketingFormCategory.MembershipV2) {
      return "membership-card";
    }
    return "e-ticket";
  }
  if (currentStepKey === "tax-receipts") {
    return "tax-receipt";
  }
  if (currentStepKey === "campaign-life-cycle") {
    return "campaign-life-cycle";
  }
  return "form";
};
